import { createStore } from 'zustand/vanilla';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import type { WebSocketMessage } from '../types';
import type { SlotSet, SlotSetListResponse } from '../types/slot-system';

interface MoFamily {
    moFamilyHash: string;
    name: string;
    description?: string;
    createdAt: string;
    memberCount?: number;
    collaboratingCount?: number;
    maxMembers?: number;
    isOwner?: boolean;
    role?: string;
    relationType?: string;
    parentFamilyHash?: string;
    totalSlots?: number;
}

interface MoFamilySlot {
    slot_number: number;
    playing_time: number;
    file_hash?: string | null;
    file_name?: string; // Deprecated
    authorized_families: string[];
    occupied?: string | null;
}

interface MoFamilyMember {
    moPeerHash: string;
    email: string;
    name: string;
    role: string;
    joinedAt: string;
}

interface MoFamilyRelationship {
    parentFamilyHash: string;
    childFamilyHash: string;
    parentFamilyName: string;
    childFamilyName: string;
    sharedSlots: number;
    sharingType: 'dedicated' | 'shared';
}

interface HierarchyNode {
    moFamilyHash: string;
    name: string;
    totalSlots: number;
    ownedSlots: number;
    children: HierarchyNode[];
    isOwner: boolean;
    sharingType?: 'dedicated' | 'shared';
}

interface MoFamilyState {
    // State
    families: Map<string, MoFamily>;
    slots: Map<string, MoFamilySlot[]>; // Legacy: active slots only
    slotsInactive: Map<string, MoFamilySlot[]>; // Legacy: inactive slots cache
    slotSets: Map<string, SlotSet[]>; // New: all slot sets for each family
    activeSlotSets: Map<string, string>; // New: active set name for each family
    members: Map<string, MoFamilyMember[]>;
    relationships: MoFamilyRelationship[];
    hierarchy: HierarchyNode[];
    selectedFamilyHash: string | null;
    isLoading: boolean;
    error: string | null;
    hierarchyNeedsReload?: boolean;
    editingSlotMode: 'active' | 'inactive'; // Legacy
    editingSlotSet: string | null; // New: specific set being edited
    slotSyncRevision: number; // Incremented on every setSlotSets call
    
    // Actions
    setFamilies: (families: MoFamily[]) => void;
    updateFamily: (familyHash: string, updates: Partial<MoFamily>) => void;
    setSlots: (familyHash: string, slots: MoFamilySlot[]) => void;
    setFamilySlots: (familyHash: string, slots: MoFamilySlot[]) => void;
    setSlotsInactive: (familyHash: string, slots: MoFamilySlot[]) => void;
    addSlot: (familyHash: string, slot: MoFamilySlot) => void;
    removeSlot: (familyHash: string, slotNumber: number) => void;
    setMembers: (familyHash: string, members: MoFamilyMember[]) => void;
    setRelationships: (relationships: MoFamilyRelationship[]) => void;
    setHierarchy: (hierarchy: HierarchyNode[]) => void;
    updateHierarchyNode: (familyHash: string, updates: Partial<HierarchyNode>) => void;
    setSelectedFamily: (familyHash: string | null) => void;
    setLoading: (isLoading: boolean) => void;
    setError: (error: string | null) => void;
    setHierarchyNeedsReload: (needsReload: boolean) => void;
    setEditingSlotMode: (mode: 'active' | 'inactive') => void;
    
    // Multi-slot system actions
    setSlotSets: (familyHash: string, sets: SlotSet[]) => void;
    setActiveSlotSet: (familyHash: string, setName: string) => void;
    setEditingSlotSet: (setName: string | null) => void;
    createSlotSet: (familyHash: string, setName: string, copyFromSet?: string) => Promise<void>;
    switchActiveSlotSet: (familyHash: string, setName: string) => Promise<void>;
    getSlotSet: (familyHash: string, setName: string) => SlotSet | undefined;
    getActiveSlotSet: (familyHash: string) => SlotSet | undefined;
    
    // WebSocket handlers
    handleWebSocketMessage: (message: WebSocketMessage) => void;
    
    // Utility functions
    getFamilyByHash: (hash: string) => MoFamily | undefined;
    getFamilySlots: (hash: string) => MoFamilySlot[];
    getFamilySlotsInactive: (hash: string) => MoFamilySlot[];
    getCurrentEditingSlots: (hash: string) => MoFamilySlot[];
    getFamilyMembers: (hash: string) => MoFamilyMember[];
    findHierarchyNode: (familyHash: string, nodes?: HierarchyNode[]) => HierarchyNode | null;
}

export const moFamilyStore = createStore<MoFamilyState>()(
    devtools(
        subscribeWithSelector((set, get) => ({
            // Initial state
            families: new Map(),
            slots: new Map(),
            slotsInactive: new Map(),
            slotSets: new Map(),
            activeSlotSets: new Map(),
            members: new Map(),
            relationships: [],
            hierarchy: [],
            selectedFamilyHash: null,
            isLoading: false,
            error: null,
            hierarchyNeedsReload: false,
            editingSlotMode: 'active',
            editingSlotSet: null,
            slotSyncRevision: 0,
            
            // Actions
            setFamilies: (families) => set(() => ({
                families: new Map(families.map(f => [f.moFamilyHash, f]))
            })),
            
            updateFamily: (familyHash, updates) => set((state) => {
                const families = new Map(state.families);
                const existing = families.get(familyHash);
                if (existing) {
                    families.set(familyHash, { ...existing, ...updates });
                }
                return { families };
            }),
            
            setSlots: (familyHash, slots) => set((state) => {
                const slotsMap = new Map(state.slots);
                slotsMap.set(familyHash, slots);
                return { slots: slotsMap };
            }),
            
            setFamilySlots: (familyHash, slots) => set((state) => {
                const slotsMap = new Map(state.slots);
                slotsMap.set(familyHash, slots);
                return { slots: slotsMap };
            }),
            
            setSlotsInactive: (familyHash, slots) => set((state) => {
                const slotsMap = new Map(state.slotsInactive);
                slotsMap.set(familyHash, slots);
                return { slotsInactive: slotsMap };
            }),
            
            addSlot: (familyHash, slot) => set((state) => {
                const slotsMap = new Map(state.slots);
                const familySlots = slotsMap.get(familyHash) || [];
                slotsMap.set(familyHash, [...familySlots, slot]);
                
                // Update family's totalSlots
                const families = new Map(state.families);
                const family = families.get(familyHash);
                if (family) {
                    families.set(familyHash, { 
                        ...family, 
                        totalSlots: (family.totalSlots || 0) + 1 
                    });
                }
                
                return { slots: slotsMap, families };
            }),
            
            removeSlot: (familyHash, slotNumber) => set((state) => {
                const slotsMap = new Map(state.slots);
                const familySlots = slotsMap.get(familyHash) || [];
                slotsMap.set(familyHash, familySlots.filter(s => s.slot_number !== slotNumber));
                
                // Update family's totalSlots
                const families = new Map(state.families);
                const family = families.get(familyHash);
                if (family && family.totalSlots) {
                    families.set(familyHash, { 
                        ...family, 
                        totalSlots: Math.max(0, family.totalSlots - 1)
                    });
                }
                
                return { slots: slotsMap, families };
            }),
            
            setMembers: (familyHash, members) => set((state) => {
                const membersMap = new Map(state.members);
                membersMap.set(familyHash, members);
                return { members: membersMap };
            }),
            
            setRelationships: (relationships) => set({ relationships }),
            
            setHierarchy: (hierarchy) => set({ hierarchy, hierarchyNeedsReload: false }),
            
            updateHierarchyNode: (familyHash, updates) => set((state) => {
                const updateNode = (nodes: HierarchyNode[]): HierarchyNode[] => {
                    return nodes.map(node => {
                        if (node.moFamilyHash === familyHash) {
                            return { ...node, ...updates };
                        }
                        if (node.children.length > 0) {
                            return { ...node, children: updateNode(node.children) };
                        }
                        return node;
                    });
                };
                
                return { hierarchy: updateNode(state.hierarchy) };
            }),
            
            setSelectedFamily: (familyHash) => set({ selectedFamilyHash: familyHash }),
            
            setLoading: (isLoading) => set({ isLoading }),
            
            setError: (error) => set({ error }),
            
            setHierarchyNeedsReload: (needsReload) => set({ hierarchyNeedsReload: needsReload }),
            
            setEditingSlotMode: (mode) => set({ editingSlotMode: mode }),
            
            // Multi-slot system implementations
            setSlotSets: (familyHash, sets) => set((state) => {
                const slotSets = new Map(state.slotSets);
                slotSets.set(familyHash, sets);
                
                // Update activeSlotSets map
                const activeSlotSets = new Map(state.activeSlotSets);
                const activeSet = sets.find(s => s.isActive);
                if (activeSet) {
                    activeSlotSets.set(familyHash, activeSet.setName);
                }
                
                // Update legacy slots for backward compatibility
                const nextRevision = state.slotSyncRevision + 1;
                if (activeSet && Array.isArray(activeSet.slots)) {
                    const slots = new Map(state.slots);
                    slots.set(familyHash, activeSet.slots);
                    return { slotSets, activeSlotSets, slots, slotSyncRevision: nextRevision };
                }

                return { slotSets, activeSlotSets, slotSyncRevision: nextRevision };
            }),
            
            setActiveSlotSet: (familyHash, setName) => set((state) => {
                const activeSlotSets = new Map(state.activeSlotSets);
                activeSlotSets.set(familyHash, setName);
                return { activeSlotSets };
            }),
            
            setEditingSlotSet: (setName) => set({ editingSlotSet: setName }),
            
            createSlotSet: async (familyHash, setName, copyFromSet) => {
                try {
                    const response = await fetch(`/mofamily/${familyHash}/slot-sets`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ setName, copyFromSet })
                    });
                    
                    if (!response.ok) {
                        const error = await response.json();
                        throw new Error(error.error || 'Failed to create slot set');
                    }
                    
                    // Reload slot sets
                    const setsResponse = await fetch(`/mofamily/${familyHash}/slot-sets`, {
                        credentials: 'include'
                    });
                    
                    if (setsResponse.ok) {
                        const data = await setsResponse.json();
                        get().setSlotSets(familyHash, data.data.sets);
                    }
                } catch (error) {
                    console.error('Error creating slot set:', error);
                    set({ error: error instanceof Error ? error.message : 'Failed to create slot set' });
                }
            },
            
            switchActiveSlotSet: async (familyHash, setName) => {
                try {
                    const response = await fetch(`/mofamily/${familyHash}/slot-sets/${setName}/activate`, {
                        method: 'POST',
                        credentials: 'include'
                    });
                    
                    if (!response.ok) {
                        const error = await response.json();
                        throw new Error(error.error || 'Failed to switch slot set');
                    }
                    
                    // Update local state
                    const state = get();
                    const sets = state.slotSets.get(familyHash);
                    if (sets) {
                        const updatedSets = sets.map(s => ({
                            ...s,
                            isActive: s.setName === setName
                        }));
                        state.setSlotSets(familyHash, updatedSets);
                    }
                } catch (error) {
                    console.error('Error switching slot set:', error);
                    set({ error: error instanceof Error ? error.message : 'Failed to switch slot set' });
                }
            },
            
            getSlotSet: (familyHash, setName) => {
                const sets = get().slotSets.get(familyHash);
                return sets?.find(s => s.setName === setName);
            },
            
            getActiveSlotSet: (familyHash) => {
                const sets = get().slotSets.get(familyHash);
                return sets?.find(s => s.isActive);
            },
            
            // WebSocket message handler
            handleWebSocketMessage: (message) => {
                const state = get();
                
                switch (message.type) {
                    case 'slot_added':
                    case 'slot_removed':
                    case 'slot_updated': {
                        const { familyHash, familyName, totalSlots, slotNumber, action } = message.payload;
                        
                        // Update family's totalSlots
                        state.updateFamily(familyHash, { totalSlots });
                        
                        // Update slots array if this is the selected family or we're viewing it
                        if (state.selectedFamilyHash === familyHash || state.slots.has(familyHash)) {
                            // Get slots from multi-slot system if available
                            let currentSlots = state.getFamilySlots(familyHash);
                            
                            // If using multi-slot system, get slots from active set
                            const slotSets = state.slotSets.get(familyHash);
                            if (slotSets && slotSets.length > 0) {
                                const activeSet = slotSets.find(s => s.isActive) || slotSets[0];
                                if (activeSet && Array.isArray(activeSet.slots)) {
                                    currentSlots = activeSet.slots;
                                }
                            }
                            
                            // Ensure currentSlots is an array
                            if (!Array.isArray(currentSlots)) {
                                currentSlots = [];
                            }
                            
                            if (action === 'add' && slotNumber) {
                                // Add new slot to the array
                                const newSlot: MoFamilySlot = {
                                    slot_number: slotNumber,
                                    playing_time: 15,
                                    file_hash: null,
                                    authorized_families: [familyHash]
                                };
                                state.setSlots(familyHash, [...currentSlots, newSlot]);
                            } else if (action === 'remove' && slotNumber) {
                                // Remove slot from the array
                                const updatedSlots = currentSlots.filter(s => s.slot_number !== slotNumber);
                                state.setSlots(familyHash, updatedSlots);
                            } else if (action === 'update' && slotNumber) {
                                // Update slot content (video assignment/removal or playing time update)
                                const fileHash = message.payload.file_hash || message.payload.fileHash || null;
                                const playingTime = message.payload.playingTime;
                                const updatedSlots = currentSlots.map(slot => {
                                    if (slot.slot_number === slotNumber) {
                                        const updatedSlot = { ...slot };
                                        if (fileHash !== undefined) {
                                            updatedSlot.file_hash = fileHash;
                                            updatedSlot.occupied = fileHash ? familyHash : null;
                                        }
                                        if (playingTime !== undefined) {
                                            updatedSlot.playing_time = playingTime;
                                        }
                                        return updatedSlot;
                                    }
                                    return slot;
                                });
                                state.setSlots(familyHash, updatedSlots);
                                
                                // Also update multi-slot system if available
                                if (slotSets && slotSets.length > 0) {
                                    const normalizedSlots = updatedSlots.map(slot => ({
                                        slot_number: slot.slot_number,
                                        playing_time: slot.playing_time,
                                        file_hash: slot.file_hash ?? null,
                                        authorized_families: slot.authorized_families,
                                        occupied: slot.occupied ?? null
                                    }));
                                    const updatedSets: SlotSet[] = slotSets.map(set => {
                                        if (set.isActive) {
                                            return {
                                                ...set,
                                                slots: normalizedSlots
                                            };
                                        }
                                        return set;
                                    });
                                    state.setSlotSets(familyHash, updatedSets);
                                }
                                
                                // Updated slot with file_hash
                            }
                        }
                        
                        // Update hierarchy node with totalSlots
                        const hierarchyNode = state.findHierarchyNode(familyHash);
                        if (hierarchyNode) {
                            // For now, assume totalSlots = ownedSlots if no delegation info
                            // This will be updated when we implement slot delegation
                            state.updateHierarchyNode(familyHash, { 
                                totalSlots,
                                ownedSlots: totalSlots // TODO: Update when delegation is implemented
                            });
                        } else {
                            // If this is the selected family and it's not in hierarchy, create a basic node
                            if (state.selectedFamilyHash === familyHash) {
                                const basicNode: HierarchyNode = {
                                    moFamilyHash: familyHash,
                                    name: familyName,
                                    totalSlots: totalSlots,
                                    ownedSlots: totalSlots,
                                    children: [],
                                    isOwner: true,
                                    sharingType: undefined
                                };
                                set({ hierarchy: [basicNode] });
                            }
                        }
                        break;
                    }
                    
                    case 'family_invitation_sent':
                    case 'family_invitation_countered': {
                        // Might need to refresh the family list
                        break;
                    }
                    
                    case 'family_relationship_created':
                    case 'family_relationship_deleted': {
                        // Need to refresh relationships and hierarchy
                        set({ hierarchyNeedsReload: true });
                        break;
                    }
                    
                    case 'slot_set_switched': {
                        const { familyHash, activeSet } = message.payload;
                        
                        // Update active slot set
                        state.setActiveSlotSet(familyHash, activeSet);
                        
                        // If we have slot sets cached, reload them from server to ensure consistency
                        if (state.slotSets.has(familyHash) || state.selectedFamilyHash === familyHash) {
                            // Fetch the latest slot sets from server
                            fetch(`/mofamily/${familyHash}/slot-sets`, {
                                credentials: 'include'
                            })
                            .then(response => response.json())
                            .then(data => {
                                if (data.success && data.data.sets) {
                                    const sets = data.data.sets;
                                    state.setSlotSets(familyHash, sets);
                                    
                                    // Find the newly active set
                                    const newActiveSet = sets.find(s => s.setName === activeSet);
                                    if (newActiveSet && Array.isArray(newActiveSet.slots)) {
                                        // Update legacy slots
                                        const slots = new Map(state.slots);
                                        slots.set(familyHash, newActiveSet.slots);
                                        set({ slots });
                                    }
                                }
                            })
                            .catch(error => {
                                console.error('Error reloading slot sets after switch:', error);
                            });
                        }
                        break;
                    }
                    
                    case 'slot_synchronized': {
                        const { familyHash, setName, syncedBy } = message.payload;
                        
                        // Reload slot sets to reflect synchronized content
                        if (state.slotSets.has(familyHash) || state.selectedFamilyHash === familyHash) {
                            fetch(`/mofamily/${familyHash}/slot-sets`, {
                                credentials: 'include'
                            })
                            .then(response => response.json())
                            .then(data => {
                                if (data.success && data.data.sets) {
                                    state.setSlotSets(familyHash, data.data.sets);
                                }
                            })
                            .catch(error => {
                                console.error('Error reloading slot sets after synchronization:', error);
                            });
                        }
                        break;
                    }
                }
            },
            
            // Utility functions
            getFamilyByHash: (hash) => {
                return get().families.get(hash);
            },
            
            getFamilySlots: (hash) => {
                const state = get();
                
                // Multi-slot system을 우선적으로 확인
                const slotSets = state.slotSets.get(hash);
                if (slotSets && slotSets.length > 0) {
                    // Active set의 슬롯을 반환
                    const activeSet = slotSets.find(s => s.isActive);
                    if (activeSet && Array.isArray(activeSet.slots)) {
                        return activeSet.slots;
                    }
                }
                
                // Legacy fallback
                return state.slots.get(hash) || [];
            },
            
            getFamilySlotsInactive: (hash) => {
                return get().slotsInactive.get(hash) || [];
            },
            
            getCurrentEditingSlots: (hash) => {
                const state = get();
                if (state.editingSlotMode === 'active') {
                    return state.getFamilySlots(hash);
                } else {
                    return state.getFamilySlotsInactive(hash);
                }
            },
            
            getFamilyMembers: (hash) => {
                return get().members.get(hash) || [];
            },
            
            findHierarchyNode: (familyHash, nodes) => {
                const searchNodes = nodes || get().hierarchy;
                
                for (const node of searchNodes) {
                    if (node.moFamilyHash === familyHash) {
                        return node;
                    }
                    if (node.children.length > 0) {
                        const found = get().findHierarchyNode(familyHash, node.children);
                        if (found) return found;
                    }
                }
                return null;
            }
        }))
    )
);